import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vacationCompensation, type VacationCompensationItem } from '@/api/vacation';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';
import { ProjectField } from '@/components/ProjectSelect';
import DocAccountsBlock from '@/shared/components/DocAccountsBlock';
import { useConfirm } from '@/components/ConfirmProvider';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import TchTable from '@/shared/components/TchTable';

const fmt2 = (v: string | number | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? 'Помилка';
}

export default function VacationCompensationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { id } = useParams();
  const [doc, setDoc] = useState<VacationCompensationItem | null>(null);
  const [days, setDays] = useState('');
  const [avg, setAvg] = useState('');
  // ч.1118 (Порядок №100, п.2): дні, право на які набуто до 31.12.2023, оплачують
  // із виплат 2023 року — окрема кількість днів і окрема середньоденна.
  const [daysBefore, setDaysBefore] = useState('0');
  const [avg2023, setAvg2023] = useState('0');
  // Проект / напрям (упр. облік) — потрапляє у субконто-3 проводок при проведенні.
  const [projectId, setProjectId] = useState('');
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const [expCode, setExpCode] = useState('92');
  // ч.879: коментар документа — зберігається на blur (як напрям).
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const docQ = useQuery({
    queryKey: ['vacation-compensation', id],
    queryFn: () => vacationCompensation.get(id!),
    enabled: !!id,
  });
  // Iм'я працівника (через org із документа).
  const empQ = useQuery({
    queryKey: ['employees', doc?.organization_id],
    queryFn: () => refs.employees(doc!.organization_id, {}),
    enabled: !!doc?.organization_id,
  });
  const empName = empQ.data?.find((e) => e.id === doc?.employee_id)?.name ?? '—';

  useEffect(() => {
    if (docQ.data) {
      setDoc(docQ.data);
      setDays(docQ.data.compensation_days);
      setAvg(docQ.data.average_daily_wage);
      setDaysBefore(docQ.data.days_before_2024 ?? '0');
      setAvg2023(docQ.data.average_daily_wage_2023 ?? '0');
      setProjectId(docQ.data.project_id ?? '');
      setComment(docQ.data.comment ?? '');
      setExpCode((docQ.data as { expense_account_code?: string }).expense_account_code ?? '92');
    }
  }, [docQ.data]);

  const isPosted = doc?.posted ?? false;

  const recalcMut = useMutation({
    mutationFn: () => vacationCompensation.update(id!, days, avg, projectId || null,
      undefined, undefined,
      { days_before_2024: daysBefore || '0', average_daily_wage_2023: avg2023 || '0' }),
    onSuccess: (r) => {
      setDoc(r); setError(null);
      setDaysBefore(r.days_before_2024 ?? '0');
      setAvg2023(r.average_daily_wage_2023 ?? '0');
    },
    onError: (e) => setError(extractError(e)),
  });
  // Зміна проекту зберігається одразу (дні/середня — поточні, без збиття).
  const projMut = useMutation({
    mutationFn: (pid: string) => vacationCompensation.update(id!, days, avg, pid || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // Зміна рахунку витрат документа — зберігається одразу.
  const expMut = useMutation({
    mutationFn: (code: string) =>
      vacationCompensation.update(id!, days, avg, undefined, code || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // ч.879: коментар — зберігається одразу (дні/середня — поточні, без збиття).
  const commentMut = useMutation({
    mutationFn: (c: string) => vacationCompensation.update(id!, days, avg, undefined, undefined, c),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  const postMut = useMutation({
    mutationFn: () => vacationCompensation.post(id!),
    onSuccess: (r) => {
      setDoc(r); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpostMut = useMutation({
    mutationFn: () => vacationCompensation.unpost(id!),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: () => vacationCompensation.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/payrolls');
    },
    onError: (e) => setError(extractError(e)),
  });

  if (docQ.isLoading || !doc) return <div className="card">Завантаження…</div>;

  // ч.876 (новий дизайн, Закон #10 + Закон #6): документ завжди збережений
  // (створюється з наказу) — DocumentToolbar без «зберегти» (savable=false);
  // посилання на наказ → «Повʼязані дії»; «розрахувати» — у заголовку блоку.
  // ч.879 (зауваження власника): наказ і так у «Звʼязаних документах»; у
  // «Повʼязаних діях» — «створити виплату» (як у нарахуванні ЗП): період
  // виплати = місяць dismiss_date (так шукає unpaid-docs).
  const _pd = new Date(doc.dismiss_date);
  const railActions = [{
    label: 'створити виплату',
    // ч.1347: виплата — зі збереженого нарахування, проведення не чекаємо.
    title: 'Виплата на підставі цього нарахування',
    disabled: false,
    onClick: () => navigate(`/payroll-payments/new?org=${doc.organization_id}&kind=COMPENSATION`
      + `&year=${_pd.getFullYear()}&month=${_pd.getMonth() + 1}&doc=${id}`),
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 0, filled: 0 },
    { id: 'calc', title: 'Розрахунок', required: 0, filled: 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нарахування (компенсація відпустки) № {doc.document_number}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/payrolls')}
          onSave={() => recalcMut.mutate()}
          savable={false}
          isPosted={isPosted}
          onPost={() => postMut.mutate()}
          postPending={postMut.isPending}
          onUnpost={() => unpostMut.mutate()}
          unpostPending={unpostMut.isPending}
          onDelete={() => {
            confirm('Видалити нарахування компенсації? (наказ про звільнення лишиться)')
              .then((ok) => { if (ok) deleteMut.mutate(); });
          }}
          deletePending={deleteMut.isPending}
          deleteTitle="Видаляє лише нарахування компенсації (наказ про звільнення лишається)"
          entriesDoc={{ type: 'doc.vacation_compensations', id: id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {isPosted && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6,
          padding: '0.4rem 0.6rem', marginBottom: '0.75rem', color: 'var(--badge-green-fg)' }}>
          ✓ Компенсацію проведено. Для редагування — спершу «розпровести».
        </div>
      )}
      {error && <div className="error">{error}</div>}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={false}
          doc={{ type: 'doc.vacation_compensations', id: id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      {/* Шапка (read-only): працівник + наказ + період. */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Працівник</label>
          <input value={empName} readOnly disabled />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата звільнення</label>
          <input value={new Date(doc.dismiss_date).toLocaleDateString('uk-UA')} readOnly disabled />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Розрахунковий період</label>
          <input value={`${doc.calc_period_start} — ${doc.calc_period_end}`} readOnly disabled />
        </div>
        <ProjectField span={2}
            value={projectId}
            organizationId={doc.organization_id}
            onChange={(v) => { setProjectId(v ?? ''); projMut.mutate(v ?? ''); }}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Розрахунок"
        actions={!isPosted ? (
          <button className="primary" disabled={recalcMut.isPending || !days || !avg}
            onClick={() => recalcMut.mutate()}
            title="Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні × середню (інвалід 8.41%, ліміт ЄСВ).">
            розрахувати
          </button>
        ) : undefined}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="field" style={{ width: 160 }}>
          <label>Днів до компенсації</label>
          <input type="number" step="0.01" value={days} disabled={isPosted}
            onChange={(e) => setDays(e.target.value)} />
        </div>
        <div className="field" style={{ width: 160 }}>
          <label>Середньоденна</label>
          <input type="number" step="0.01" value={avg} disabled={isPosted}
            onChange={(e) => setAvg(e.target.value)} />
        </div>
        <div className="field" style={{ width: 200 }}>
          <label>з них набуто до 31.12.2023</label>
          <input type="number" step="0.01" min={0} value={daysBefore} disabled={isPosted}
            title="Компенсацію за відпустки, право на які набуто до 31 грудня 2023 року, рахують із виплат 2023 року (Порядок №100, п. 2)"
            onChange={(e) => setDaysBefore(e.target.value)} />
        </div>
        <div className="field" style={{ width: 200 }}>
          <label>Середньоденна за 2023 рік</label>
          <input type="number" step="0.01" min={0} value={avg2023}
            disabled={isPosted || Number(daysBefore || 0) <= 0}
            onChange={(e) => setAvg2023(e.target.value)} />
        </div>
      </div>
      {doc.calc_note && (
        <div style={{ margin: '0 0 0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
          background: 'var(--bg-soft)', border: '1px solid var(--border)', fontSize: '0.8rem',
          whiteSpace: 'pre-line' }}>
          {doc.calc_note}
        </div>
      )}
      {/* Закладки: розрахунок ↔ рахунки обліку (рішення власника 2026-07-07);
          ч.876 — канонічні seg-tabs. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div className="seg-tabs">
          <button type="button" className={docTab === 'calc' ? 'active' : ''}
            onClick={() => setDocTab('calc')}>Розрахунок</button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={docTab === 'accounts' ? 'active' : ''}
            onClick={() => setDocTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>
      {docTab === 'accounts' && (
        <DocAccountsBlock
          organizationId={doc.organization_id}
          employeeIds={[doc.employee_id]}
          paymentKind="COMPENSATION"
          headerCodes={{ expense: expCode, payroll: '661', pdfo: '6411', wz: '642', esv: '651' }}
          expenseCode={expCode}
          onExpenseChange={(v) => { setExpCode(v); if (v) expMut.mutate(v); }}
          disabled={isPosted}
          nameOf={() => empName}
        />
      )}
      {/* Таблична частина — дизайн як у нарахуванні ЗП. */}
      {docTab === 'calc' && (
      <div style={{ overflowX: 'auto' }}>
        <TchTable>
          <thead>
            <tr style={{ textAlign: 'right' }}>
              <th style={{ textAlign: 'left' }}>Працівник</th>
              <th style={{ width: 70 }}>Днів</th>
              <th style={{ width: 95 }}>Середня</th>
              <th style={{ width: 95 }}>Брутто</th>
              <th style={{ width: 95 }}>ПДФО</th>
              <th style={{ width: 95 }}>ВЗ</th>
              <th style={{ width: 95 }}>ЄСВ</th>
              <th style={{ width: 100 }}>До виплати</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{empName}</td>
              <td style={{ textAlign: 'right' }}>{fmt2(doc.compensation_days)}</td>
              <td style={{ textAlign: 'right' }}>{fmt2(doc.average_daily_wage)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt2(doc.compensation_gross_amount)}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.pdfo_amount)}
                    style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                  <InfoQ title={`ПДФО — ${trimNum(doc.pdfo_rate ?? '18')}% від брутто.`} />
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.wz_amount)}
                    style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                  <InfoQ title={`Військовий збір — ${trimNum(doc.wz_rate ?? '5')}% від брутто.`} />
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.esv_amount)}
                    style={{ textAlign: 'right', width: '100%', background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
                  <InfoQ title={esvTipDoc(doc.esv_amount, doc.esv_rate ?? '22')} />
                </div>
              </td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt2(doc.net_amount)}</td>
            </tr>
          </tbody>
        </TchTable>
      </div>
      )}
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
        Виплата компенсації — через «Виплата нарахувань» (борг видно у «Нарахування виплат → Борги»).
      </p>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={comment} disabled={isPosted}
                 onChange={(e) => setComment(e.target.value)}
                 onBlur={() => { if ((doc.comment ?? '') !== comment) commentMut.mutate(comment); }} />
        </div>
        <div>
          <AttachmentsPanel organizationId={doc.organization_id}
            documentType={'doc.vacation_compensations'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
